import React from 'react';
import { Link } from 'react-router-dom';
import ImageSlider from './ImageSlider';
import './HomepageComponents.css';

// Features Section Component
export const FeaturesSection = ({ title, features }) => {
  if (!features || features.length === 0) return null;

  return (
    <section className="features-section">
      <div className="container">
        {title && <h2 className="section-title">{title}</h2>}
        <div className="features-grid">
          {features.map((feature, index) => (
            <div key={index} className="feature-card">
              <div className="feature-icon">{feature.icon}</div>
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-description">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section Component
export const TestimonialsSection = ({ title, testimonials }) => {
  if (!testimonials || testimonials.length === 0) return null;

  return (
    <section className="testimonials-section">
      <div className="container">
        {title && <h2 className="section-title">{title}</h2>}
        <div className="testimonials-grid">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="testimonial-card">
              {testimonial.image && (
                <img
                  src={testimonial.image}
                  alt={testimonial.name}
                  className="testimonial-image"
                />
              )}
              <div className="testimonial-content">
                <div className="testimonial-rating">
                  {[...Array(testimonial.rating || 5)].map((_, i) => (
                    <span key={i} className="star">⭐</span>
                  ))}
                </div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <strong>{testimonial.name}</strong>
                  {testimonial.role && (
                    <span className="testimonial-role">{testimonial.role}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section Component
export const CTASection = ({ data }) => {
  if (!data) return null;

  return (
    <section className="cta-section">
      <div className="container">
        <div className="cta-content">
          <h2 className="cta-title">{data.title}</h2>
          {data.description && (
            <p className="cta-description">{data.description}</p>
          )}
          <div className="cta-buttons">
            {data.button_text && data.button_link && (
              <Link to={data.button_link} className="btn btn-primary btn-large">
                {data.button_text}
              </Link>
            )}
            {data.secondary_button_text && data.secondary_button_link && (
              <Link to={data.secondary_button_link} className="btn btn-secondary btn-large">
                {data.secondary_button_text}
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Stats Section Component
export const StatsSection = ({ title, stats }) => {
  if (!stats || stats.length === 0) return null;

  return (
    <section className="stats-section">
      <div className="container">
        {title && <h2 className="section-title">{title}</h2>}
        <div className="stats-grid">
          {stats.map((stat, index) => (
            <div key={index} className="stat-card">
              <div className="stat-number">{stat.number}</div>
              <div className="stat-label">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Main Component Renderer
const HomepageComponents = ({ components }) => {
  if (!components || components.length === 0) return null;

  return (
    <div className="homepage-components">
      {components.map((component, index) => {
        try {
          const content = typeof component.content === 'string'
            ? JSON.parse(component.content)
            : component.content;

          switch (component.type) {
            case 'slider':
              return <ImageSlider key={index} slides={content} />;

            case 'features':
              return (
                <FeaturesSection
                  key={index}
                  title={component.title}
                  features={content}
                />
              );

            case 'testimonials':
              return (
                <TestimonialsSection
                  key={index}
                  title={component.title}
                  testimonials={content}
                />
              );

            case 'cta':
              return <CTASection key={index} data={content} />;

            case 'stats':
              return (
                <StatsSection
                  key={index}
                  title={component.title}
                  stats={content}
                />
              );

            default:
              return null;
          }
        } catch (error) {
          console.error('Error rendering component:', error);
          return null;
        }
      })}
    </div>
  );
};

export default HomepageComponents;
